// src/components/feed/AnnotationDetailsModal.tsx
//
// "More details" Dialog surfaced from a callout on AnnotatedImage.
// Opened only when the active annotation has a non-empty
// `more_details` body — the server pipeline reserves that field
// for genuinely rich features (a famous artwork, a historically
// significant element), so the modal exists to let those features
// breathe instead of being squeezed into the inline callout.
//
// Mirrors the visual language of the inline callout:
//   • Header tinted with the category color (same palette used in
//     the AnnotatedImage dot legend / chat chips).
//   • Confidence-aware voice on the title (low-confidence
//     identifications are prefixed with "Appears to be…", same as
//     in the callout).
//   • Short explanation kept at the top as a lede.
//   • Wikipedia thumbnail inline (when an article exists for this
//     entity) — uses the existing `useEntityImage` hook that
//     already powers the inline-entity-image cards in chat. Image
//     resolves async and renders nothing on failure (graceful
//     degradation, same as elsewhere in the app), so the modal
//     looks complete with or without a Wikipedia hit.
//   • Long-form `more_details` body below.
//   • Tap the Wikipedia thumbnail → opens a nested lightbox so the
//     user can see the full-resolution image and click through to
//     the article for deeper reading.
//
// z-index is left to MUI's default Dialog stacking. When opened
// from inside the lightbox Dialog (FeedItemCard's full-image view)
// MUI assigns this Dialog a higher z than the lightbox, so it
// renders above without manual zIndex juggling.

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  ButtonBase,
  Skeleton,
  Link,
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { useT } from '../../i18n/useT';
import { useEntityImage } from '../../hooks/useEntityImage';
import type { PhotoAnnotation } from '../../feed/usePhotoAnnotations';

interface Props {
  /** When non-null, the modal is open and shows this annotation. */
  annotation: PhotoAnnotation | null;
  /** Color to tint the header — same palette the parent uses for
   *  dots / chips. Passed in (rather than recomputed here) so the
   *  visual identity is consistent with whatever the parent
   *  decides for its category palette. */
  accentColor: string;
  onClose: () => void;
}

export const AnnotationDetailsModal: React.FC<Props> = ({
  annotation,
  accentColor,
  onClose,
}) => {
  const t = useT();
  const open = !!annotation;
  const confidence = annotation?.confidence ?? 0.5;
  const showHedge = confidence < 0.7;
  // Suppress Wikipedia lookups for low-confidence identifications:
  // if we're already saying "Appears to be" we shouldn't pull a
  // Wikipedia image for a label we're not sure about — that
  // grounds the user in something potentially wrong. Threshold
  // mirrors the hedge threshold so the visual story is consistent.
  const allowWikiLookup = !showHedge;
  const [wikiLightboxOpen, setWikiLightboxOpen] = useState(false);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        // Stop clicks on the backdrop from also bubbling up to a
        // parent Dialog (the lightbox case in FeedItemCard) which
        // would close BOTH dialogs in a single tap.
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: 2,
            // Subtle category-tinted top border so the modal feels
            // continuous with the dot the user just tapped.
            borderTop: `4px solid ${accentColor}`,
          },
        }}
      >
        {annotation && (
          <DialogContent
            sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header: title + close. Title carries the category-colored
                accent so it's visually anchored to the dot the user
                tapped on the image. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                px: 2.5,
                pt: 2.25,
                pb: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: accentColor,
                    lineHeight: 1.25,
                    wordBreak: 'break-word',
                  }}
                >
                  {showHedge && (
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 500,
                        color: 'text.secondary',
                        mr: 0.75,
                      }}
                    >
                      {t('Appears to be')}
                    </Box>
                  )}
                  {annotation.label}
                </Typography>
                <Box sx={{ mt: 0.75 }}>
                  <Chip
                    size="small"
                    label={annotation.category}
                    sx={{
                      bgcolor: `${accentColor}1A`,
                      color: accentColor,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      height: 22,
                      fontSize: '0.72rem',
                    }}
                  />
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={onClose}
                aria-label={t('Close')}
                sx={{ mt: -0.25, mr: -0.5 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* Body. Short explanation as lede above the long form,
                so the reader sees a one-sentence summary before
                deciding to read the full body. */}
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.5,
                  color: 'text.primary',
                  mb: 1.25,
                }}
              >
                {annotation.explanation}
              </Typography>

              {/* Wikipedia image card. Looked up by label, scoped to
                  the open=true case via the WikiImage child so the
                  hook only fires while the modal is mounted. */}
              {allowWikiLookup && (
                <WikiImage
                  label={annotation.label}
                  category={annotation.category}
                  onLightboxRequest={() => setWikiLightboxOpen(true)}
                />
              )}

              {annotation.more_details && (
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    mt: 1.5,
                  }}
                >
                  {annotation.more_details}
                </Typography>
              )}
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Wikipedia lightbox — separate Dialog so the underlying
          details modal stays open behind it and the user can dismiss
          back into the modal. Mounts only when the user has tapped
          the thumbnail. The `WikiLightbox` child is responsible for
          re-resolving the entity image so that opening the lightbox
          a second time after a remount is reliable. */}
      {annotation && allowWikiLookup && (
        <WikiLightbox
          open={wikiLightboxOpen}
          onClose={() => setWikiLightboxOpen(false)}
          label={annotation.label}
          category={annotation.category}
        />
      )}
    </>
  );
};

// ──────────────────────────────────────────────────────────────────
// Wikipedia thumbnail card.
// ──────────────────────────────────────────────────────────────────

interface WikiImageProps {
  label: string;
  category: string;
  onLightboxRequest: () => void;
}

const WikiImage: React.FC<WikiImageProps> = ({
  label,
  category,
  onLightboxRequest,
}) => {
  const t = useT();
  // useEntityImage is the same hook InteractiveContent uses for
  // inline entity images. It returns `null` while loading AND on
  // failure, so we briefly show a low-effort skeleton while the
  // network round-trip happens, then render nothing if Wikipedia
  // doesn't have an article for this label. We can't distinguish
  // "loading" from "no article" without a flag; the skeleton is
  // opt-in via a short timer to avoid showing it indefinitely on
  // entities that simply don't resolve.
  const image = useEntityImage(category, label);
  const [showSkeleton, setShowSkeleton] = useState(true);

  React.useEffect(() => {
    // After 1.2s, give up on the loading state — if Wikipedia
    // hasn't returned by then it's almost certainly a "no article"
    // miss and showing the skeleton longer just creates dead air.
    const t = setTimeout(() => setShowSkeleton(false), 1200);
    return () => clearTimeout(t);
  }, []);

  if (!image) {
    if (!showSkeleton) return null;
    return (
      <Box sx={{ mt: 1, mb: 0.5 }}>
        <Skeleton
          variant="rectangular"
          height={180}
          sx={{ borderRadius: 1.5 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          onLightboxRequest();
        }}
        sx={{
          width: '100%',
          borderRadius: 1.5,
          overflow: 'hidden',
          display: 'block',
          textAlign: 'left',
          bgcolor: 'action.hover',
          cursor: 'zoom-in',
          transition:
            'transform 0.05s ease, box-shadow 0.15s ease',
          '&:active': { transform: 'scale(0.997)' },
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          },
        }}
      >
        <Box
          component="img"
          src={image.thumbnailUrl}
          alt={image.title}
          loading="lazy"
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: 220,
            objectFit: 'cover',
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </ButtonBase>

      {/* Caption + Wikipedia attribution row. */}
      <Box
        sx={{
          mt: 0.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {image.description && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.72rem',
                color: 'text.secondary',
                lineHeight: 1.35,
              }}
            >
              {image.description}
            </Typography>
          )}
        </Box>
        <Link
          href={image.articleUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            fontSize: '0.65rem',
            opacity: 0.75,
            flexShrink: 0,
            '&:hover': { opacity: 1 },
          }}
          title={t('Source: Wikipedia')}
        >
          {t('Wikipedia')}
          <OpenInNew sx={{ fontSize: 11 }} />
        </Link>
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────
// Wikipedia full-resolution lightbox.
// ──────────────────────────────────────────────────────────────────

interface WikiLightboxProps {
  open: boolean;
  onClose: () => void;
  label: string;
  category: string;
}

const WikiLightbox: React.FC<WikiLightboxProps> = ({
  open,
  onClose,
  label,
  category,
}) => {
  const t = useT();
  // Re-resolves through the same hook — the cache makes this free
  // after the first hit. Lets the lightbox render the full-res URL
  // independent of the thumbnail card's lifecycle.
  const image = useEntityImage(category, label);
  if (!image) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0,0,0,0.92)',
          boxShadow: 'none',
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '95vh' },
        },
      }}
    >
      <DialogContent
        onClick={onClose}
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          cursor: 'zoom-out',
          minHeight: { xs: '100vh', sm: 'auto' },
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'common.white',
            bgcolor: 'rgba(0,0,0,0.4)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            zIndex: 1,
          }}
          aria-label={t('Close')}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={image.fullUrl}
          alt={image.title}
          sx={{
            maxWidth: '100%',
            maxHeight: { xs: '100vh', sm: '95vh' },
            objectFit: 'contain',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
